import type { VisualizerLayer, VisualizerType } from '../domain/schemas';
import { defaultLayer } from './layers';

/**
 * Built-in visualizer presets (Phase 4C). Original, procedural looks: every preset is a
 * list of ordinary, editable layers plus a backdrop colour. Applying a preset copies these
 * layers into a session (with fresh ids); the definitions here are never modified.
 * Flash is off in every preset (photosensitivity); users can enable it explicitly.
 */

export interface PresetBackdrop {
  background: string;
  gradient: { to: string; angle: number } | null;
}

export interface VisualizerPreset {
  id: string;
  name: string;
  description: string;
  backdrop: PresetBackdrop;
  /** Layers for a scene of this size (ids and seeds are placeholders, replaced on apply). */
  layers(scene: { width: number; height: number }): VisualizerLayer[];
}

type Size = { width: number; height: number };
type Layer<T extends VisualizerType> = Extract<VisualizerLayer, { type: T }>;
type Patch<T extends VisualizerType> = Partial<Omit<Layer<T>, 'type' | 'params' | 'style' | 'response'>> & {
  params?: Partial<Layer<T>['params']>;
  style?: Partial<Layer<T>['style']>;
  response?: Partial<Layer<T>['response']>;
};

/** A default layer with overrides (params and response merged, not replaced). */
function layer<T extends VisualizerType>(type: T, scene: Size, n: number, patch: Patch<T> = {}): VisualizerLayer {
  const base = defaultLayer(type, scene, `preset_${n}`, 1000 + n) as Extract<VisualizerLayer, { type: T }>;
  return {
    ...base,
    ...patch,
    style: { ...base.style, ...patch.style },
    response: { ...base.response, ...patch.response },
    params: { ...base.params, ...patch.params },
  } as VisualizerLayer;
}

export const VISUALIZER_PRESETS: readonly VisualizerPreset[] = [
  {
    id: 'neon-phonk',
    name: 'Neon Phonk',
    description: 'Purple and pink radial bars with pulsing rings and a soft glow.',
    backdrop: { background: '#12021f', gradient: { to: '#05010a', angle: 90 } },
    layers: (s) => [
      layer('pulse', s, 1, { name: 'Kick rings', blend: 'add', style: { primary: '#ec4899', secondary: '#a855f7', glow: 0.4 }, params: { maxRings: 5, expand: 1.1, lineWidth: 5, core: 0.25 } }),
      layer('radial', s, 2, { name: 'Radial spectrum', blend: 'add', style: { primary: '#a855f7', secondary: '#f472b6', glow: 0.5 }, params: { count: 96, innerRadius: 0.42, length: 0.55, direction: 'outward', symmetry: 2, spin: 0.25 } }),
    ],
  },
  {
    id: 'dark-bass',
    name: 'Dark Bass',
    description: 'Minimal black, heavy low-frequency pulses and red accents.',
    backdrop: { background: '#050505', gradient: null },
    layers: (s) => [
      layer('pulse', s, 1, { name: 'Bass pulse', style: { primary: '#dc2626', secondary: '#7f1d1d', glow: 0.25 }, response: { categories: ['kick', 'bass808'], decay: 0.9, band: 'low' }, params: { shape: 'ring', maxRings: 3, expand: 1.3, lineWidth: 10, core: 0.35 } }),
      layer('bars', s, 2, {
        name: 'Low bars',
        y: s.height * 0.86,
        height: Math.round(s.height * 0.25),
        style: { primary: '#ef4444', secondary: '#450a0a', glow: 0.15 },
        response: { sensitivity: 1.2, smoothing: 0.5, decay: 0.4 },
        params: { count: 24, gap: 0.4, mirrored: true, minHeight: 0.01 },
      }),
    ],
  },
  {
    id: 'cyber-city',
    name: 'Cyber City',
    description: 'A neon skyline of equalizer bars with rhythmic lighting.',
    backdrop: { background: '#020617', gradient: { to: '#1e1b4b', angle: 90 } },
    layers: (s) => [
      layer('grid', s, 1, { name: 'Street grid', style: { primary: '#22d3ee', secondary: '#6366f1', glow: 0.2 }, params: { lines: 12, speed: 0.5, horizon: 0.05, pulse: 0.4 } }),
      layer('bars', s, 2, {
        name: 'Skyline',
        y: s.height * 0.55,
        height: Math.round(s.height * 0.5),
        width: s.width,
        style: { primary: '#1e293b', secondary: '#312e81', glow: 0 },
        response: { sensitivity: 0.35, smoothing: 0.9, decay: 1 },
        params: { count: 28, gap: 0.08, minHeight: 0.35 },
      }),
      layer('bars', s, 3, {
        name: 'Window lights',
        y: s.height * 0.55,
        height: Math.round(s.height * 0.5),
        width: s.width,
        blend: 'add',
        style: { primary: '#22d3ee', secondary: '#f0abfc', glow: 0.5 },
        response: { sensitivity: 1, smoothing: 0.3, decay: 0.3 },
        params: { count: 56, gap: 0.55, minHeight: 0.02 },
      }),
    ],
  },
  {
    id: 'retro-wave',
    name: 'Retro Wave',
    description: 'Synthwave colours, a scrolling geometric grid and a symmetrical waveform.',
    backdrop: { background: '#1a0533', gradient: { to: '#ff6ad5', angle: 90 } },
    layers: (s) => [
      layer('grid', s, 1, { name: 'Horizon grid', style: { primary: '#ff6ad5', secondary: '#26e2ff', glow: 0.4 }, params: { lines: 18, speed: 1, horizon: 0.0, pulse: 0.6 } }),
      layer('waveform', s, 2, { name: 'Mirrored wave', y: s.height * 0.35, blend: 'add', style: { primary: '#26e2ff', secondary: '#ff6ad5', glow: 0.5 }, params: { mirrored: true, thickness: 3, windowMs: 80, points: 160 } }),
    ],
  },
  {
    id: 'minimal-spectrum',
    name: 'Minimal Spectrum',
    description: 'Clean spectrum bars with restrained motion.',
    backdrop: { background: '#0a0a0a', gradient: null },
    layers: (s) => [
      layer('bars', s, 1, {
        name: 'Spectrum',
        y: s.height * 0.62,
        height: Math.round(s.height * 0.35),
        style: { primary: '#f4f4f5', secondary: '#a1a1aa', glow: 0 },
        response: { sensitivity: 0.8, smoothing: 0.6, decay: 0.35 },
        params: { count: 64, gap: 0.35, minHeight: 0.01 },
      }),
    ],
  },
  {
    id: 'particle-storm',
    name: 'Particle Storm',
    description: 'Beat-driven particles bursting from expanding pulses.',
    backdrop: { background: '#030712', gradient: { to: '#111827', angle: 90 } },
    layers: (s) => [
      layer('pulse', s, 1, { name: 'Pulses', blend: 'add', style: { primary: '#38bdf8', secondary: '#818cf8', glow: 0.35 }, response: { categories: ['kick', 'snare'] }, params: { maxRings: 6, lineWidth: 3, core: 0 } }),
      layer('particles', s, 2, {
        name: 'Burst',
        blend: 'add',
        y: s.height / 2,
        height: 40,
        width: 40,
        style: { primary: '#facc15', secondary: '#f97316', glow: 0.3 },
        response: { categories: ['kick', 'snare'] },
        params: { count: 300, perEvent: 24, spread: 360, speed: 260, gravity: 60, lifetime: 1.6, size: 3 },
      }),
      layer('particles', s, 3, {
        name: 'Drift',
        y: s.height,
        height: 20,
        width: s.width,
        style: { primary: '#a5b4fc', secondary: '#e0e7ff', glow: 0 },
        response: { source: 'measured', band: 'high' },
        params: { count: 150, perEvent: 0, rate: 40, spread: 30, direction: -90, speed: 90, gravity: -10, lifetime: 4, size: 2 },
      }),
    ],
  },
];

export function presetById(id: string): VisualizerPreset | undefined {
  return VISUALIZER_PRESETS.find((p) => p.id === id);
}
